import { Box, Typography, Paper, Button, LinearProgress, Chip, Alert, Card, CardContent, CardMedia, CardActionArea, Grid } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { ROUTES } from '../config/constants';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TranscribeIcon from '@mui/icons-material/Subtitles';
import TranslateIcon from '@mui/icons-material/Translate';
import FolderIcon from '@mui/icons-material/Folder';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { useSubscriptionStatus } from '../hooks/useSubscription';
import { useProjects } from '../hooks/useProjects';
import { useListeningTimeStats } from '../hooks/useListeningTime';
import { SUBSCRIPTION_TIERS, getRemainingQuota } from '../config/subscriptionTiers';
import type { SubscriptionTier } from '../config/subscriptionTiers';
import { useMemo } from 'react';
import { TestErrorButton } from '../components/common/TestErrorButton';
import { Onboarding } from '../components/onboarding/Onboarding';
import { useOnboarding } from '../hooks/useOnboarding';
import { PodcastDiscoveryTutorial } from '../components/onboarding/PodcastDiscoveryTutorial';
import { usePodcastDiscoveryTutorial } from '../hooks/usePodcastDiscoveryTutorial';
import { ExperimentsAnnouncementTour } from '../components/onboarding/ExperimentsAnnouncementTour';
import { useExperimentsAnnouncement } from '../hooks/useExperimentsAnnouncement';

export const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const { data: subscriptionData, isLoading: subLoading, error: subError } = useSubscriptionStatus();
  const { projects } = useProjects();
  const { data: listeningStats } = useListeningTimeStats();
  const { showOnboarding, completeOnboarding, skipOnboarding } = useOnboarding({ 
    projectCount: projects?.length || 0 
  });

  // Calculate total episodes
  const totalEpisodes = useMemo(() => {
    if (!projects) return 0;
    return projects.reduce((sum, project) => sum + (project.podcastEpisodes?.length || 0), 0);
  }, [projects]);

  const { showTutorial: showPodcastTutorial, completeTutorial: completePodcastTutorial, skipTutorial: skipPodcastTutorial } = usePodcastDiscoveryTutorial({
    projectCount: projects?.length || 0,
    episodeCount: totalEpisodes
  });

  const {
    showTour: showExperimentsAnnouncement,
    completeTour: completeExperimentsAnnouncement,
    skipTour: skipExperimentsAnnouncement,
  } = useExperimentsAnnouncement();

  const displayName = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'User';
  
  // Format seconds into hours and minutes
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  // Default to free tier if no data
  const rawTier = subscriptionData?.tier || 'free';
  const currentTier: SubscriptionTier = (rawTier === 'basic' || rawTier === 'premium') ? 'pro' : rawTier as SubscriptionTier;
  const tierInfo = SUBSCRIPTION_TIERS[currentTier];
  const usageStats = subscriptionData?.usage;
  const limits = subscriptionData?.limits;

  // Find the most recently accessed episode across all projects
  const lastEpisode = useMemo<{ episode: any; projectId: number; projectName: string } | null>(() => {
    if (!projects || projects.length === 0) return null;
    
    let mostRecent: { episode: any; projectId: number; projectName: string } | null = null;
    let mostRecentDate = 0;

    projects.forEach(project => {
      if (project.podcastEpisodes && project.podcastEpisodes.length > 0) {
        project.podcastEpisodes.forEach(episode => {
          // Check lastAccessed (camelCase as returned by backend)
          const dateStr = episode.lastAccessed || episode.createdAt;
          const episodeDate = dateStr ? new Date(dateStr).getTime() : 0;
          
          // Only consider episodes with valid timestamps (not default 0001-01-01)
          if (episodeDate > 0 && (episodeDate > mostRecentDate || mostRecent === null)) {
            mostRecentDate = episodeDate;
            mostRecent = {
              episode,
              projectId: project.id,
              projectName: project.projectName
            };
          }
        });
      }
    });

    return mostRecent;
  }, [projects]);

  // Read the last-visited YouTube video recorded by VideoPlayer.tsx.
  const lastYtVideo = useMemo<{
    videoId: string;
    projectId: number;
    title: string;
    thumbnailUrl: string | null;
    channelName: string | null;
    youtubeVideoId: string;
    timestamp: number;
  } | null>(() => {
    try {
      const raw = localStorage.getItem('scribelate-last-yt-video');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Pick whichever was accessed more recently: podcast episode or YouTube video.
  type ContinueLearningItem =
    | { type: 'episode'; episode: any; projectId: number; projectName: string }
    | { type: 'video'; videoId: string; projectId: number; title: string; thumbnailUrl: string | null; channelName: string | null };

  const continueLearning = useMemo<ContinueLearningItem | null>(() => {
    const episodeTime = lastEpisode
      ? new Date(lastEpisode.episode.lastAccessed || lastEpisode.episode.createdAt || 0).getTime()
      : 0;
    const videoTime = lastYtVideo?.timestamp ?? 0;
    if (videoTime > episodeTime && lastYtVideo) {
      return { type: 'video', videoId: lastYtVideo.videoId, projectId: lastYtVideo.projectId, title: lastYtVideo.title, thumbnailUrl: lastYtVideo.thumbnailUrl, channelName: lastYtVideo.channelName };
    }
    if (lastEpisode) {
      return { type: 'episode', ...lastEpisode };
    }
    return null;
  }, [lastEpisode, lastYtVideo]);

  const renderUsageBar = (label: string, used: number, limit: number | 'unlimited', color: 'primary' | 'secondary' | 'success' | 'warning' = 'primary') => {
    if (limit === 'unlimited' || limit === -1) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {used}
          </Typography>
          <Typography variant="caption" color="success.main">
            {t('unlimited', { ns: 'subscription' })}
          </Typography>
        </Box>
      );
    }

    const percentage = (used / limit) * 100;
    const isNearLimit = percentage >= 80;
    const remaining = getRemainingQuota(used, limit);
    const remainingCount = typeof remaining === 'number' ? remaining : 0;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight="bold" color={isNearLimit ? 'warning.main' : 'inherit'}>
            {used} / {limit}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={isNearLimit ? 'warning' : color}
          sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t('remaining', { ns: 'subscription', count: remainingCount })}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {/* Onboarding Modal */}
      <Onboarding
        open={showOnboarding}
        onComplete={completeOnboarding}
        onSkip={skipOnboarding}
      />

      {/* Podcast Discovery Tutorial - shows after first project */}
      <PodcastDiscoveryTutorial
        open={showPodcastTutorial}
        onComplete={completePodcastTutorial}
        onSkip={skipPodcastTutorial}
      />

      {/* Experiments announcement - one-time, never stacked with the other tutorials */}
      {!showOnboarding && !showPodcastTutorial && (
        <ExperimentsAnnouncementTour
          open={showExperimentsAnnouncement}
          onComplete={completeExperimentsAnnouncement}
          onSkip={skipExperimentsAnnouncement}
        />
      )}

      <Typography variant="h3" component="h1" gutterBottom>
        {t('welcomeBack', { name: displayName })}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('subtitle')}
      </Typography>

      {/* Learning Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Listening Time by Language */}
        {listeningStats && listeningStats.languages.length > 0 && listeningStats.languages.map((langStats, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={langStats.language}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: index % 2 === 0 ? 'primary.light' : 'secondary.light' }}>
              <AccessTimeIcon sx={{ fontSize: 48, color: index % 2 === 0 ? 'primary.contrastText' : 'secondary.contrastText', mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color={index % 2 === 0 ? 'primary.contrastText' : 'secondary.contrastText'}>
                {formatTime(langStats.totalSeconds)}
              </Typography>
              <Typography variant="body2" color={index % 2 === 0 ? 'primary.contrastText' : 'secondary.contrastText'}>
                {t('stats.totalListeningTime')} ({langStats.language})
              </Typography>
              <Typography variant="caption" color={index % 2 === 0 ? 'primary.contrastText' : 'secondary.contrastText'} display="block" sx={{ mt: 1 }}>
                {t('stats.thisMonth')}: {formatTime(langStats.monthlySeconds)}
              </Typography>
            </Paper>
          </Grid>
        ))}

        {/* Projects */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              }
            }}
            onClick={() => navigate(ROUTES.PROJECTS)}
          >
            <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              {projects?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stats.projects')}
            </Typography>
          </Paper>
        </Grid>

        {/* Episodes */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <HeadphonesIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              {totalEpisodes}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stats.episodes')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Secondary Stats Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Transcriptions */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <TranscribeIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              {usageStats?.autoTranscriptionsCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stats.transcriptions')}
            </Typography>
          </Paper>
        </Grid>

        {/* Translations */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <TranslateIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              {usageStats?.autoTranslationsCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stats.translations')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Continue Learning & Quick Actions Row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Continue Learning Card */}
        {continueLearning && continueLearning.type === 'episode' && (
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ height: '100%', overflow: 'hidden' }}>
              <CardActionArea
                onClick={() => navigate(`/player/${continueLearning.projectId}/${continueLearning.episode.id}?focus=true`)}
                sx={{ height: '100%' }}
              >
                <Card sx={{ display: 'flex', p: 2, height: '100%' }}>
                  {continueLearning.episode.imageUrl && (
                    <CardMedia
                      component="img"
                      sx={{ width: 120, height: 120, borderRadius: 2 }}
                      image={continueLearning.episode.imageUrl}
                      alt={continueLearning.episode.episodeName}
                    />
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, ml: 2 }}>
                    <CardContent sx={{ flex: '1 0 auto', p: 0 }}>
                      <Chip label={t('continueLearning')} color="primary" size="small" sx={{ mb: 1 }} />
                      <Typography component="div" variant="h6" noWrap>
                        {continueLearning.episode.name || 'Untitled Episode'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {continueLearning.projectName}
                      </Typography>
                    </CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <PlayArrowIcon sx={{ mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2" color="primary">
                        {t('resumeListening')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </CardActionArea>
            </Paper>
          </Box>
        )}

        {continueLearning && continueLearning.type === 'video' && (
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ height: '100%', overflow: 'hidden' }}>
              <CardActionArea
                onClick={() => navigate(`/projects/${continueLearning.projectId}/videos/${continueLearning.videoId}`)}
                sx={{ height: '100%' }}
              >
                <Card sx={{ display: 'flex', p: 2, height: '100%' }}>
                  {continueLearning.thumbnailUrl ? (
                    <CardMedia
                      component="img"
                      sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover' }}
                      image={continueLearning.thumbnailUrl}
                      alt={continueLearning.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <OndemandVideoIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, ml: 2 }}>
                    <CardContent sx={{ flex: '1 0 auto', p: 0 }}>
                      <Chip label={t('continueLearning')} color="primary" size="small" sx={{ mb: 1 }} />
                      <Typography component="div" variant="h6" noWrap>
                        {continueLearning.title}
                      </Typography>
                      {continueLearning.channelName && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {continueLearning.channelName}
                        </Typography>
                      )}
                    </CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <PlayArrowIcon sx={{ mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2" color="primary">
                        {t('resumeWatching')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </CardActionArea>
            </Paper>
          </Box>
        )}

        {/* Quick Actions Card */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              {t('quickActions')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, flex: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate(ROUTES.PROJECTS)}
                startIcon={<FolderIcon />}
                fullWidth
                size="large"
              >
                {t('myProjects', { ns: 'projects' })}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(ROUTES.SUBSCRIPTION)}
                startIcon={<TrendingUpIcon />}
                fullWidth
                size="large"
              >
                {t('viewSubscription')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Subscription Usage Overview */}
      {!subLoading && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {tierInfo.name} {t('plan', { ns: 'subscription' })}
                {currentTier !== 'free' && (
                  <Chip label={t('active')} color="success" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('usageThisMonth')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color={currentTier === 'free' ? 'primary' : 'inherit'}
              onClick={() => navigate(ROUTES.SUBSCRIPTION)}
              size="small"
            >
              {currentTier === 'free' ? t('upgrade', { ns: 'subscription' }) : t('managePlan')}
            </Button>
          </Box>

          {subError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('unableToLoadDetails')}
            </Alert>
          )}

          {limits && usageStats && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderUsageBar(
                  t('autoTranscriptions'),
                  usageStats.autoTranscriptionsCount || 0,
                  limits.autoTranscriptions,
                  'primary'
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {renderUsageBar(
                  t('autoTranslations'),
                  usageStats.autoTranslationsCount || 0,
                  limits.autoTranslations,
                  'success'
                )}
              </Grid>
            </Grid>
          )}

          {currentTier === 'free' && usageStats && limits && (
            (usageStats.autoTranscriptionsCount >= limits.autoTranscriptions * 0.8 ||
             usageStats.autoTranslationsCount >= limits.autoTranslations * 0.8) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('lowCreditsWarning')}
              </Alert>
            )
          )}
        </Paper>
      )}

      {/* Test Error Boundary - Only visible in dev mode */}
      <TestErrorButton />
    </Box>
  );
};
